import React, { useEffect, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle } from "lucide-react";
import { usePageExit } from "@/lib/pageTransition";

gsap.registerPlugin(ScrollTrigger);

const ACCENT = "#E11D2E";
const BG = "#050505";
const MUTED = "#BDBDBD";

const LOGO =
  "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/ssol55p1_Logo%20bramsgraphic%20white.png";

/**
 * Generic service detail page used by lighter services.
 * Receives a config object describing the service.
 *
 * config = {
 *   testidPrefix: "sm",        // unique prefix for data-testids
 *   serviceNumber: "05",       // displayed at top right
 *   eyebrow: "Design Social Media",
 *   title: "Design",
 *   titleAccent: "Réseaux Sociaux",
 *   subtitle: "...",
 *   paragraphs: ["...", "..."],
 *   bullets: [{ title, desc }, ...],     // "Nos prestations"
 *   realisations: ["str", "str", ...],   // tags
 *   mockupSrc: "https://...",
 *   mockupAlt: "...",
 *   badges: [{ k, v }, { k, v }, { k, v }],
 *   ctaLabel: "Demander un devis",
 *   bottomLine: <JSX>...</JSX>           // bottom strip headline
 * }
 */
export default function ServiceDetailTemplate({ config }) {
  const rootRef = useRef(null);
  const overlayRef = useRef(null);
  const eyebrowRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const descRef = useRef(null);
  const bulletsRef = useRef(null);
  const realisationsRef = useRef(null);
  const ctaRef = useRef(null);
  const mockupRef = useRef(null);
  const mockupWrapRef = useRef(null);
  const exit = usePageExit();

  const prefix = config.testidPrefix;

  useEffect(() => {
    document.body.classList.add("dark");
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        onComplete: () => {
          if (overlayRef.current) overlayRef.current.style.pointerEvents = "none";
        },
      });

      const textTargets = [
        eyebrowRef.current,
        titleRef.current,
        subtitleRef.current,
        descRef.current,
        bulletsRef.current,
        realisationsRef.current,
        ctaRef.current,
      ].filter(Boolean);

      gsap.set(textTargets, { opacity: 0, x: -80 });
      gsap.set(mockupWrapRef.current, { opacity: 0, x: 120, scale: 0.92, rotation: 2 });

      const tl = gsap.timeline({ delay: 0.15 });
      tl.to(textTargets, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.15,
      });
      tl.to(
        mockupWrapRef.current,
        {
          opacity: 1,
          x: 0,
          scale: 1,
          rotation: 0,
          duration: 1,
          ease: "power4.out",
        },
        "-=0.9"
      );

      gsap.to(mockupRef.current, {
        y: -8,
        duration: 5,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      ScrollTrigger.batch(".sd-reveal", {
        start: "top 85%",
        once: true,
        onEnter: (els) =>
          gsap.fromTo(
            els,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", stagger: 0.08 }
          ),
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  const handleMockupEnter = () => {
    gsap.to(mockupRef.current, {
      scale: 1.03,
      rotationX: 2,
      rotationY: -3,
      duration: 0.5,
      ease: "power3.out",
      transformPerspective: 1000,
    });
  };
  const handleMockupLeave = () => {
    gsap.to(mockupRef.current, {
      scale: 1,
      rotationX: 0,
      rotationY: 0,
      duration: 0.5,
      ease: "power3.out",
    });
  };

  return (
    <div
      ref={rootRef}
      data-testid={`${prefix}-page`}
      className="relative min-h-screen text-white overflow-x-hidden"
      style={{ backgroundColor: BG, fontFamily: "'Geist Sans','Inter',system-ui,sans-serif" }}
    >
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[60]"
        style={{ backgroundColor: BG, opacity: 1 }}
        aria-hidden="true"
      />

      <div
        className="pointer-events-none fixed inset-0"
        aria-hidden="true"
        style={{
          background: `radial-gradient(60% 40% at 50% 0%, ${ACCENT}10 0%, transparent 60%),
                       radial-gradient(50% 35% at 100% 100%, ${ACCENT}0a 0%, transparent 60%)`,
          zIndex: 0,
        }}
      />

      <header className="relative z-20 max-w-7xl mx-auto px-6 lg:px-10 pt-7 md:pt-10 flex items-center justify-between">
        <Link
          to="/"
          onClick={(e) => { e.preventDefault(); exit("realisations"); }}
          data-testid={`${prefix}-back-home`}
          className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] opacity-75 hover:opacity-100 transition-all"
        >
          <span className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center transition-all group-hover:-translate-x-1 group-hover:border-white/50">
            <ArrowLeft size={14} />
          </span>
          Retour
        </Link>
        <Link to="/" className="flex items-center" data-testid={`${prefix}-logo`}>
          <img src={LOGO} alt="Brams Graphic Design" className="h-12 md:h-14 object-contain" />
        </Link>
        <div className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] opacity-60">
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: ACCENT }} />
          Service · {config.serviceNumber}
        </div>
      </header>

      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div
              ref={eyebrowRef}
              className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em]"
              style={{ color: MUTED }}
            >
              <span className="h-px w-8" style={{ background: ACCENT }} />
              {config.eyebrow}
            </div>

            <h1
              ref={titleRef}
              data-testid={`${prefix}-title`}
              className="mt-6 font-display font-bold tracking-tighter leading-[1.02] text-5xl sm:text-6xl lg:text-7xl"
              style={{ letterSpacing: "-0.04em" }}
            >
              {config.title} <span style={{ color: ACCENT }}>{config.titleAccent}</span>
            </h1>

            <p
              ref={subtitleRef}
              data-testid={`${prefix}-subtitle`}
              className="mt-6 text-lg md:text-xl font-light"
              style={{ color: MUTED }}
            >
              {config.subtitle}
            </p>

            <div ref={descRef} data-testid={`${prefix}-description`} className="mt-10 space-y-5 max-w-2xl">
              {config.paragraphs.map((p, idx) => (
                <p
                  key={idx}
                  className="text-base md:text-[17px] leading-relaxed font-light text-white/80"
                  dangerouslySetInnerHTML={{ __html: p }}
                />
              ))}
            </div>

            {/* Prestations */}
            <div className="mt-16">
              <div className="flex items-end justify-between gap-6 mb-8">
                <h2 className="font-display font-semibold text-2xl md:text-3xl tracking-tight">
                  Nos prestations
                </h2>
                <span
                  className="hidden md:inline-block text-[10px] uppercase tracking-[0.3em]"
                  style={{ color: MUTED }}
                >
                  {String(config.bullets.length).padStart(2, "0")} livrables
                </span>
              </div>

              <div
                ref={bulletsRef}
                data-testid={`${prefix}-services-grid`}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4"
              >
                {config.bullets.map((b, i) => {
                  const Icon = b.icon;
                  return (
                    <div
                      key={b.title}
                      data-testid={`${prefix}-service-${i}`}
                      className="sd-reveal group relative p-6 transition-all duration-500 ease-out"
                      style={{
                        background: "#0B0B0B",
                        border: "1px solid rgba(255,255,255,0.06)",
                        borderRadius: 18,
                      }}
                    >
                      <span
                        className="absolute left-0 top-0 h-[2px] w-0 group-hover:w-full transition-all duration-700"
                        style={{ background: ACCENT }}
                      />
                      <div className="flex items-start gap-4">
                        {Icon && (
                          <div
                            className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110"
                            style={{
                              background: "rgba(255,255,255,0.03)",
                              border: "1px solid rgba(255,255,255,0.08)",
                            }}
                          >
                            <Icon size={18} strokeWidth={1.5} style={{ color: ACCENT }} />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-display font-semibold tracking-tight text-base md:text-lg">
                            {b.title}
                          </h3>
                          <p className="mt-2 text-sm leading-relaxed font-light" style={{ color: MUTED }}>
                            {b.desc}
                          </p>
                        </div>
                        <Circle
                          size={10}
                          strokeWidth={1.2}
                          className="opacity-30 group-hover:opacity-100 transition-opacity"
                          style={{ color: ACCENT }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Réalisations associées */}
            {config.realisations?.length > 0 && (
              <div
                ref={realisationsRef}
                data-testid={`${prefix}-realisations`}
                className="mt-14"
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-8" style={{ background: ACCENT }} />
                  <span className="text-[10px] uppercase tracking-[0.4em]" style={{ color: MUTED }}>
                    Quelques réalisations
                  </span>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {config.realisations.map((r) => (
                    <li
                      key={r}
                      className="flex items-center gap-3 text-[15px] font-light text-white/85"
                    >
                      <span
                        className="inline-flex w-6 h-6 items-center justify-center rounded-full"
                        style={{
                          background: "rgba(225,29,46,0.08)",
                          border: `1px solid ${ACCENT}33`,
                        }}
                      >
                        <CheckCircle2 size={12} style={{ color: ACCENT }} />
                      </span>
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTA */}
            <div ref={ctaRef} className="mt-14">
              <Link
                to="/#contact"
                onClick={(e) => { e.preventDefault(); exit("contact"); }}
                data-testid={`${prefix}-cta-quote`}
                className="group relative inline-flex items-center gap-3 rounded-full pl-8 pr-3 py-3.5 font-medium text-sm tracking-wide transition-all duration-300 ease-out hover:scale-[1.04] active:scale-95"
                style={{
                  background: ACCENT,
                  color: "#fff",
                  boxShadow: `0 10px 40px -10px ${ACCENT}99`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 18px 60px -10px ${ACCENT}cc, 0 0 0 6px rgba(225,29,46,0.12)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = `0 10px 40px -10px ${ACCENT}99`;
                }}
              >
                <span className="uppercase tracking-[0.25em]">{config.ctaLabel}</span>
                <span
                  className="flex items-center justify-center w-10 h-10 rounded-full transition-transform duration-500 group-hover:translate-x-1"
                  style={{ background: "rgba(0,0,0,0.25)" }}
                >
                  <ArrowRight size={16} />
                </span>
              </Link>
              <p className="mt-4 text-xs uppercase tracking-[0.3em]" style={{ color: MUTED }}>
                Devis gratuit · Réponse sous 48 h
              </p>
            </div>
          </div>

          {/* RIGHT — Mockup */}
          <div className="lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-10">
            <div ref={mockupWrapRef} className="relative" style={{ perspective: 1200 }}>
              <div
                className="absolute -inset-10 rounded-[40px] pointer-events-none"
                style={{
                  background: `radial-gradient(closest-side, ${ACCENT}26, transparent 70%)`,
                  filter: "blur(40px)",
                }}
                aria-hidden="true"
              />

              <div
                ref={mockupRef}
                onMouseEnter={handleMockupEnter}
                onMouseLeave={handleMockupLeave}
                className="relative overflow-hidden"
                data-testid={`${prefix}-mockup`}
                style={{
                  borderRadius: 24,
                  background: "#0A0A0A",
                  border: "1px solid rgba(255,255,255,0.06)",
                  boxShadow:
                    "0 30px 80px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04)",
                  transformStyle: "preserve-3d",
                  willChange: "transform",
                }}
              >
                <img
                  src={config.mockupSrc}
                  alt={config.mockupAlt}
                  className="block w-full h-auto select-none"
                  draggable={false}
                  loading="eager"
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 25%)",
                  }}
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 70%, rgba(0,0,0,0.45) 100%)",
                  }}
                />
              </div>

              <div
                aria-hidden="true"
                className="mt-3 mx-8 h-10 opacity-50"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(225,29,46,0.18) 0%, transparent 70%)",
                  filter: "blur(10px)",
                }}
              />

              <div className="sd-reveal mt-8 grid grid-cols-3 gap-3">
                {config.badges.map((b) => (
                  <div
                    key={b.k}
                    className="p-4 text-center"
                    style={{
                      background: "#0B0B0B",
                      border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: 18,
                    }}
                  >
                    <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: MUTED }}>
                      {b.k}
                    </div>
                    <div className="mt-1 font-display font-semibold text-sm tracking-tight">
                      {b.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA strip */}
      <section
        className="sd-reveal relative z-10 border-t border-white/[0.06] py-16 md:py-20"
        style={{ background: "#070707" }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <span className="text-[10px] uppercase tracking-[0.4em]" style={{ color: MUTED }}>
              Prêt à démarrer ?
            </span>
            <h3 className="mt-3 font-display font-semibold text-3xl md:text-4xl tracking-tighter">
              {config.bottomLine}
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/#contact"
              onClick={(e) => { e.preventDefault(); exit("contact"); }}
              data-testid={`${prefix}-cta-bottom-contact`}
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-medium text-sm transition-all duration-300 hover:scale-[1.03] active:scale-95"
              style={{ background: "#fff", color: "#000" }}
            >
              Me contacter
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/"
              onClick={(e) => { e.preventDefault(); exit("realisations"); }}
              data-testid={`${prefix}-cta-bottom-home`}
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-medium text-sm transition-all duration-300 hover:scale-[1.03] active:scale-95 border border-white/20 hover:bg-white/5"
            >
              Voir d&apos;autres services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
